import { useState, useEffect, useCallback } from 'react';
import { SlotCoreDevServerService } from '@renderer/services/slot-core/SlotCoreDevServerService';

/**
 * @param enabled - When false, skips all polling (default: false).
 *                  Pass true only when iframe preview mode is active.
 */
export function useSlotCoreDevServer(enabled = false) {
  const [isRunning, setIsRunning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkHealth = useCallback(async () => {
    try {
      // First check if the game server itself responds
      await fetch('http://localhost:8080', { mode: 'no-cors', signal: AbortSignal.timeout(3000) });
      setIsRunning(true);
      setError(null);
    } catch {
      // Fallback: check via ai-proxy backend status
      try {
        const status = await SlotCoreDevServerService.getStatus();
        setIsRunning(status.running);
        setError(status.running ? null : 'Dev server not running at localhost:8080');
      } catch {
        setIsRunning(false);
        setError('Dev server not running at localhost:8080');
      }
    }
  }, []);

  /**
   * Start the dev server automatically via the ai-proxy backend.
   * @param projectPath - Absolute path to the user's project directory
   */
  const startServer = useCallback(async (projectPath: string) => {
    if (isRunning || isStarting) return;

    setIsStarting(true);
    setError(null);

    try {
      const status = await SlotCoreDevServerService.start(projectPath);
      if (status.running) {
        // Wait a bit for Vite to fully initialize, then check health
        await new Promise(resolve => setTimeout(resolve, 3000));
        await checkHealth();
      } else {
        setError(status.error ?? 'Failed to start dev server');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start dev server');
    } finally {
      setIsStarting(false);
    }
  }, [isRunning, isStarting, checkHealth]);

  /**
   * Stop the dev server via the ai-proxy backend.
   */
  const stopServer = useCallback(async () => {
    try {
      await SlotCoreDevServerService.stop();
      setIsRunning(false);
      setError(null);
    } catch {
      // Ignore stop errors
    }
  }, []);

  // Only poll when enabled (iframe preview mode is active)
  useEffect(() => {
    if (!enabled) return;

    checkHealth();
    const interval = setInterval(checkHealth, 5000);
    return () => clearInterval(interval);
  }, [enabled, checkHealth]);

  return { isRunning, isStarting, error, checkHealth, startServer, stopServer };
}
